// Flacht geparste MIG/AHB-Dokumente zu render- und durchsuchbaren Knoten und
// findet den besten Treffer zum Changelog-`ort` (Token-Heuristik).

export interface DocNode {
  /** Anzeige-Text der Zeile (Codes + Name). */
  label: string;
  /** Einrück-Tiefe fürs Rendering. */
  depth: number;
  /** lowercase-Suchtext für das Ort-Matching. */
  searchText: string;
  /** Knotenart fürs Styling. */
  kind: "group" | "segment" | "data_element" | "line";
}

type AnyObj = Record<string, unknown>;

function txt(...parts: Array<unknown>): string {
  return parts.filter(Boolean).map(String).join(" ").trim();
}

export function flattenMigDoc(doc: AnyObj): DocNode[] {
  const out: DocNode[] = [];
  const walkDes = (des: AnyObj[], depth: number) => {
    for (const de of des ?? []) {
      if (de.kind === "composite") {
        out.push(node("data_element", depth, txt(de.key, de.name)));
        walkDes((de.data_elements as AnyObj[]) ?? [], depth + 1);
      } else {
        out.push(node("data_element", depth, txt(de.data_element, de.name)));
      }
    }
  };
  const walk = (nodes: AnyObj[], depth: number, sg: string) => {
    for (const n of nodes ?? []) {
      if (n.kind === "group") {
        const key = String(n.key ?? "");
        out.push(node("group", depth, txt(key, n.name)));
        walk((n.children as AnyObj[]) ?? [], depth + 1, key || sg);
      } else if (n.kind === "segment") {
        out.push(node("segment", depth, txt(sg, n.counter, n.segment_code, n.name)));
        walkDes((n.data_elements as AnyObj[]) ?? [], depth + 1);
      }
    }
  };
  const node = (kind: DocNode["kind"], depth: number, label: string): DocNode => ({
    kind, depth, label, searchText: label.toLowerCase(),
  });
  walk((doc.segments as AnyObj[]) ?? [], 0, "");
  return out;
}

export function flattenAhbDoc(doc: AnyObj): DocNode[] {
  return (((doc.lines as AnyObj[]) ?? [])).map((l) => {
    const label = txt(l.segment_group_key, l.segment_code, l.data_element, l.value_pool_entry, l.name, l.ahb_expression);
    return { kind: "line" as const, depth: l.segment_group_key ? 1 : 0, label, searchText: label.toLowerCase() };
  });
}

/** Bester Knoten zum ort-Text: Token-Overlap-Score; -1 wenn kein Token trifft. */
export function findBestNodeIndex(nodes: DocNode[], ort: string): number {
  const tokens = ort.toLowerCase().split(/[^0-9a-zäöüß]+/).filter((t) => t.length >= 2);
  if (!tokens.length) return -1;
  let best = -1;
  let bestScore = 0;
  nodes.forEach((n, i) => {
    let score = 0;
    for (const t of tokens) if (n.searchText.includes(t)) score += t.length >= 3 ? 2 : 1;
    // Tiefe Knoten (DE/Zeile) bei Gleichstand bevorzugen — präziserer Sprung
    if (score > bestScore || (score === bestScore && score > 0 && best >= 0 && n.depth > nodes[best].depth)) {
      best = i;
      bestScore = score;
    }
  });
  return bestScore >= 3 ? best : -1;
}
